'use client';

import { useTheme } from '@mui/material';
import { addHours } from 'date-fns';
import { useTranslation } from 'react-i18next';
import DatePicker, { DateObject, DatePickerProps } from 'react-multi-date-picker';

import { darkNeutral, lightNeutral } from '@/colors';
import { InputField } from '@/components/atoms/input-field';
import { dateFormats } from '@/constants/date-formats';
import { MonthsTranslationKeys, WeekDaysTranslationKeys } from '@/constants/dates';
import './date-range-select.styles.css';

interface Props extends DatePickerProps<false, true> {
  numberOfMonths?: number;
  onlyMonthPicker?: boolean;
  onlyYearPicker?: boolean;
  minDate?: Date;
  maxDate?: Date;
  value: DateObject[];
  format?: string;
}

export const DATE_RANGE_DATE_FORMAT = `${
  dateFormats.month
}/${dateFormats.day.toUpperCase()}/${dateFormats?.year.toUpperCase()}`;

const DateRangeSelect = ({
  onlyMonthPicker,
  onlyYearPicker,
  numberOfMonths,
  minDate,
  maxDate,
  value,
  format,
  ...rest
}: Props) => {
  const { t } = useTranslation(['common']);

  const {
    palette: { mode },
  } = useTheme();

  return (
    <DatePicker
      {...rest}
      range
      rangeHover
      onlyMonthPicker={onlyMonthPicker}
      onlyYearPicker={onlyYearPicker}
      numberOfMonths={numberOfMonths}
      value={value}
      className={mode === 'dark' ? 'dark-custom-calendar' : 'custom-calendar'}
      dateSeparator=" - "
      format={format}
      minDate={minDate}
      // WE WANT TO START THE WEEK FROM MONDAY WHICH INDEX IS 1
      weekStartDayIndex={1}
      // WE ARE ADDING 1 HOUR TO THE MAX DATE TO ALLOW USER TO SELECT THE SAME DATE AS FIRST ONE SELECTED FOR INSTANCE IF USER SELECTS JANUARY AS FIRST DATE THEN HE CAN SELECT JANUARY AS SECOND DATE
      maxDate={maxDate || addHours(new Date(), 1)}
      months={Object.values(MonthsTranslationKeys).map(day => t(`common:months.${day}`))}
      weekDays={Object.values(WeekDaysTranslationKeys).map(day => t(`common:weekDays.${day}`))}
      mapDays={({ date, today, selectedDate, isSameDate }) => {
        const props = {
          style: {
            borderRadius: '50px',
            backgroundColor: 'transparent',
            border: 'none',
          },
        };

        if (
          (isSameDate(date, selectedDate?.[0]) && !isSameDate(selectedDate?.[0], today)) ||
          (isSameDate(date, selectedDate?.[1]) && !isSameDate(selectedDate?.[1], today))
        ) {
          props.style = {
            ...props.style,
            backgroundColor: mode === 'dark' ? darkNeutral[100] : lightNeutral[100],
            border: ' 1px solid #5046e4',
            // @ts-expect-error TYPESCRIPT THROWING ERROR THAT COLOR IS NOT A VALID PROPERTY BUT WE CAN PASS IT BECAUSE IT IS A VALID PROPERTY
            color: mode === 'dark' ? '#fff' : '#000',
          };
        }

        return props;
      }}
      render={
        <InputField
          placeholder={t('common:selectDateRange')}
          sx={{ width: '240px', height: 42 }}
          slotProps={{
            htmlInput: {
              style: { height: '42px' },
            },
            input: {
              readOnly: true,
            },
          }}
        />
      }
    />
  );
};

export default DateRangeSelect;
